import React, { useState, useEffect, Suspense, lazy } from 'react';
import { NextUIProvider, Spinner } from '@nextui-org/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import MainLayout from './layouts/MainLayout';
import LiveLayout from './layouts/LiveLayout';
// Startroute bleibt statisch importiert: LCP-kritisch, kein Lazy-Roundtrip.
import HomePage from './pages/HomePage';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import AnalyticsTracker from './components/AnalyticsTracker';
import CookieConsentBanner from './components/CookieConsentBanner';
import AdminRoute from './components/admin/AdminRoute';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import healthService from './services/healthService';
import { AuthProvider } from './contexts/AuthContext';
import { SearchExplorerProvider } from './contexts/SearchExplorerContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Route-basiertes Code-Splitting: alle Seiten (außer HomePage) werden lazy
// geladen, damit main.js klein bleibt. Alle Seiten haben default exports.
const TournamentPage = lazy(() => import('./pages/TournamentPage'));
const TournamentDetailPage = lazy(() => import('./pages/TournamentDetailPage'));
const WorldCupOfDartsPage = lazy(() => import('./pages/WorldCupOfDartsPage'));
const ProTournamentInfoPage = lazy(() => import('./pages/ProTournamentInfoPage'));
const ClubsPage = lazy(() => import('./pages/ClubsPage'));
const ClubDetailPage = lazy(() => import('./pages/ClubDetailPage'));
const PubsPage = lazy(() => import('./pages/PubsPage'));
const PubDetailPage = lazy(() => import('./pages/PubDetailPage'));
const ShopsPage = lazy(() => import('./pages/ShopsPage'));
const ShopDetailPage = lazy(() => import('./pages/ShopDetailPage'));
const SeriesPage = lazy(() => import('./pages/SeriesPage'));
const SeriesDetailPage = lazy(() => import('./pages/SeriesDetailPage'));
const RankingsPage = lazy(() => import('./pages/RankingsPage'));
const ForumPage = lazy(() => import('./pages/ForumPage'));
const ForumTopicPage = lazy(() => import('./pages/ForumTopicPage'));
const SpielePage = lazy(() => import('./pages/SpielePage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const NewsDetailPage = lazy(() => import('./pages/NewsDetailPage'));
const PlayerProfilePage = lazy(() => import('./pages/PlayerProfilePage'));
const PlayersDirectoryPage = lazy(() => import('./pages/PlayersDirectoryPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const ManagementPage = lazy(() => import('./pages/ManagementPage'));
const EditClubPage = lazy(() => import('./pages/EditClubPage'));
const CreateClubPage = lazy(() => import('./pages/CreateClubPage'));
// Liga & Verband (2026-09-16): generische Seiten mit type-Prop
const OrgListingsPage = lazy(() => import('./pages/OrgListingsPage'));
const OrgListingDetailPage = lazy(() => import('./pages/OrgListingDetailPage'));
const CreateOrgListingPage = lazy(() => import('./pages/CreateOrgListingPage'));
const EditOrgListingPage = lazy(() => import('./pages/EditOrgListingPage'));
const EditPubPage = lazy(() => import('./pages/EditPubPage'));
const CreatePubPage = lazy(() => import('./pages/CreatePubPage'));
const EditShopPage = lazy(() => import('./pages/EditShopPage'));
const CreateShopPage = lazy(() => import('./pages/CreateShopPage'));
const EditSeriesPage = lazy(() => import('./pages/EditSeriesPage'));
const CreateSeriesPage = lazy(() => import('./pages/CreateSeriesPage'));
const EditTournamentPage = lazy(() => import('./pages/EditTournamentPage'));
const TournamentEmailTemplatesPage = lazy(() => import('./pages/TournamentEmailTemplatesPage'));
const ManageEntriesPage = lazy(() => import('./pages/ManageEntriesPage'));
const ManageTeamEntriesPage = lazy(() => import('./pages/ManageTeamEntriesPage'));
const CreateTournamentPage = lazy(() => import('./pages/CreateTournamentPage'));
const TournamentOpPage = lazy(() => import('./pages/TournamentOpPage'));
const ScorerPage = lazy(() => import('./pages/ScorerPage'));
const LiveViewPage = lazy(() => import('./pages/LiveViewPage'));
const LiveTournamentsPage = lazy(() => import('./pages/LiveTournamentsPage'));
const LiveMatchPage = lazy(() => import('./pages/LiveMatchPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const TournamentRegistrationPage = lazy(() => import('./pages/TournamentRegistrationPage'));
const MyRegistrationsPage = lazy(() => import('./pages/MyRegistrationsPage'));
const EditRegistrationPage = lazy(() => import('./pages/EditRegistrationPage'));
const TeamInvitationPage = lazy(() => import('./pages/TeamInvitationPage'));
const ChatsPage = lazy(() => import('./pages/ChatsPage'));
const SetupDetailPage = lazy(() => import('./pages/SetupDetailPage'));
const DartRoomDetailPage = lazy(() => import('./pages/DartRoomDetailPage'));
const NearbyPage = lazy(() => import('./pages/NearbyPage'));
// Standort-Landing-Pages (2026-09-04): /darten-in, /darten-in/:slug, /<kategorie>-in/:slug
const PlacesIndexPage = lazy(() => import('./pages/PlacesIndexPage'));
const PlaceLandingPage = lazy(() => import('./pages/PlaceLandingPage'));
const StatsExplorerPage = lazy(() => import('./pages/StatsExplorerPage'));
const DcStatsPage = lazy(() => import('./pages/DcStatsPage'));
const PlayerExplorerPage = lazy(() => import('./pages/PlayerExplorerPage'));
const SeasonStatsPage = lazy(() => import('./pages/SeasonStatsPage'));
const PredictionGamesPage = lazy(() => import('./pages/PredictionGamesPage'));
const PredictionGameDetailPage = lazy(() => import('./pages/PredictionGameDetailPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const MatchDetailPage = lazy(() => import('./pages/MatchDetailPage'));
const PdcTournamentsPage = lazy(() => import('./pages/PdcTournamentsPage'));
const LobbiesPage = lazy(() => import('./pages/LobbiesPage'));
const LobbyDetailPage = lazy(() => import('./pages/LobbyDetailPage'));
const EventsTimelinePage = lazy(() => import('./pages/EventsTimelinePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FeedPage = lazy(() => import('./pages/FeedPage'));
const PostDetailPage = lazy(() => import('./pages/PostDetailPage'));
const DynamicPage = lazy(() => import('./pages/DynamicPage'));
const InfoPagesPage = lazy(() => import('./pages/InfoPagesPage'));
const InfoPageDetailPage = lazy(() => import('./pages/InfoPageDetailPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const HelpArticlePage = lazy(() => import('./pages/HelpArticlePage'));
const AdminDailyJournalPage = lazy(() => import('./pages/admin/AdminDailyJournalPage'));
const AdminWatchdogPage = lazy(() => import('./pages/admin/AdminWatchdogPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminNewsAutomationPage = lazy(() => import('./pages/admin/AdminNewsAutomationPage'));
const AdminBotDetectionPage = lazy(() => import('./pages/admin/AdminBotDetectionPage'));
const AdminClaudeSessionsPage = lazy(() => import('./pages/admin/AdminClaudeSessionsPage'));
const AdminContactRequestsPage = lazy(() => import('./pages/admin/AdminContactRequestsPage'));
const AdminNewsArticlesPage = lazy(() => import('./pages/admin/AdminNewsArticlesPage'));
const AdminLiveBlogsPage = lazy(() => import('./pages/admin/AdminLiveBlogsPage'));
const AdminSitemapPage = lazy(() => import('./pages/admin/AdminSitemapPage'));
const AdminProTournamentsPage = lazy(() => import('./pages/admin/AdminProTournamentsPage'));
const AdminPlayerMatchingPage = lazy(() => import('./pages/admin/AdminPlayerMatchingPage'));
const AdminProRankingsPage = lazy(() => import('./pages/admin/AdminProRankingsPage'));
const AdminInfoPagesPage = lazy(() => import('./pages/admin/AdminInfoPagesPage'));
const AdminPushCampaignsPage = lazy(() => import('./pages/admin/AdminPushCampaignsPage'));
const AdminEmailCampaignsPage = lazy(() => import('./pages/admin/AdminEmailCampaignsPage'));
const AdminHelpCenterPage = lazy(() => import('./pages/admin/AdminHelpCenterPage'));
const AdminHelpArticleEditorPage = lazy(() => import('./pages/admin/AdminHelpArticleEditorPage'));
const AdminPredictionGamesPage = lazy(() => import('./pages/admin/AdminPredictionGamesPage'));
const AdminPlanningPage = lazy(() => import('./pages/admin/AdminPlanningPage'));
const AdminPlanningCategoryPage = lazy(() => import('./pages/admin/AdminPlanningCategoryPage'));
const AdminPredictionGameDetailPage = lazy(() => import('./pages/admin/AdminPredictionGameDetailPage'));
const AdminPagesPage = lazy(() => import('./pages/admin/AdminPagesPage'));
const AdminPageEditorPage = lazy(() => import('./pages/admin/AdminPageEditorPage'));
const AdminSlidersPage = lazy(() => import('./pages/admin/AdminSlidersPage'));
const AdminSliderDetailPage = lazy(() => import('./pages/admin/AdminSliderDetailPage'));
const AdminConfigPage = lazy(() => import('./pages/admin/AdminConfigPage'));
const AdminServerStoragePage = lazy(() => import('./pages/admin/AdminServerStoragePage'));
const AdminDrawerPage = lazy(() => import('./pages/admin/AdminDrawerPage'));
const AdminBackupPage = lazy(() => import('./pages/admin/AdminBackupPage'));
const AdminChangelogPage = lazy(() => import('./pages/admin/AdminChangelogPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AdminTournamentsPage = lazy(() => import('./pages/admin/AdminTournamentsPage'));
const AdminListingsPage = lazy(() => import('./pages/admin/AdminListingsPage'));
// Benutzer / Dart Rooms / Setups (2026-09-04)
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminDartRoomsPage = lazy(() => import('./pages/admin/AdminDartRoomsPage'));
const AdminSetupsPage = lazy(() => import('./pages/admin/AdminSetupsPage'));
const AdminPostsPage = lazy(() => import('./pages/admin/AdminPostsPage'));
const AdminTournamentOpsPage = lazy(() => import('./pages/admin/AdminTournamentOpsPage'));
const AdminContentSectionsPage = lazy(() => import('./pages/admin/AdminContentSectionsPage'));
const AdminSeoPage = lazy(() => import('./pages/admin/AdminSeoPage'));
const AdminPushMessagesPage = lazy(() => import('./pages/admin/AdminPushMessagesPage'));
const AdminReportsPage = lazy(() => import('./pages/admin/AdminReportsPage'));

/** Fallback beim Nachladen eines Routen-Chunks: zentrierter Spinner,
 *  volle Höhe auf dunklem Seitenhintergrund → kein Layout-Shift. */
const RouteFallback: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center bg-zinc-950">
    <Spinner size="lg" color="primary" aria-label="Seite wird geladen" />
  </div>
);

function App() {
  const [isBackendHealthy, setIsBackendHealthy] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Health Service starten
    healthService.startHealthCheck(10000); // 10 Sekunden Intervall

    // Health Status Listener
    const unsubscribe = healthService.onHealthChange((isHealthy) => {
      setIsBackendHealthy(isHealthy);
      if (!isInitialized) {
        setIsInitialized(true);
      }
    });

    // Cleanup bei Unmount
    return () => {
      unsubscribe();
      healthService.cleanup();
    };
  }, [isInitialized]);

  const handleRetry = () => {
    // Retry wird vom Health Service automatisch gehandelt
    console.log('Manual retry triggered');
  };

  return (
    <HelmetProvider>
     {/* Globaler Fallback-Titel: greift, solange eine Seite (noch) keinen eigenen
         <title> rendert (z.B. Lade-/Fehler-Zustände) — verhindert leere Browser-Tabs. */}
     {/* Muss zum statischen <title> in public/index.html passen (SEO-Fallback) */}
     <Helmet defaultTitle="Dart-Turniere, Live-Scores & Vereine finden | DartCloud" />
     <ThemeProvider>
      <NextUIProvider>
        <Router>
          <ScrollToTop />
          <CookieConsentBanner />
          <AuthProvider>
           {/* Braucht Router UND AuthProvider (User-ID fürs First-Party-Tracking) */}
           <AnalyticsTracker />
           <SearchExplorerProvider>
            {/* Theme (dark/light) kommt über die Klasse auf <html> – siehe ThemeContext */}
            <div className="bg-black text-white min-h-screen">
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<HomePage />} />
                  <Route path="spiele" element={<SpielePage />} />
                  {/* Öffentliche Übersicht aller laufenden Turniersoftware-Turniere.
                      2026-09-02: Die SEO-Audit-Weiterleitung /live → /spiele war FALSCH
                      (Profi-Spiele ≠ Live-Turniere der Turniersoftware) — Marcel: Navbar
                      „Turniere" führt hierher. Route + .htaccess wiederhergestellt. */}
                  <Route path="live" element={<LiveTournamentsPage />} />
                  <Route path="spiel/:eventId" element={<MatchDetailPage />} />
                  <Route path="pdc-turniere" element={<PdcTournamentsPage />} />
                  <Route path="turniere" element={<TournamentPage />} />
                  <Route path="turniere/pdc-world-cup-of-darts" element={<WorldCupOfDartsPage />} />
                  <Route path="turniere/:slug" element={<ProTournamentInfoPage />} />
                  <Route path="turnier/:slug" element={<TournamentDetailPage />} />
                  <Route path="turnier/:slug/anmelden" element={<TournamentRegistrationPage />} />
                  <Route path="team-einladung/:code" element={<TeamInvitationPage />} />
                  {/* Alias: Backend-E-Mails verlinken auf /team/join/:code */}
                  <Route path="team/join/:code" element={<TeamInvitationPage />} />
                  <Route path="tippspiele" element={<PredictionGamesPage />} />
                  <Route path="tippspiele/:id" element={<PredictionGameDetailPage />} />
                  <Route path="setup/:setupId" element={<SetupDetailPage />} />
                  <Route path="dart-room/:id" element={<DartRoomDetailPage />} />
                  <Route path="suchen" element={<SearchPage />} />
                  <Route path="kontakt" element={<ContactPage />} />
                  <Route path="dartcloud/:slug" element={<DynamicPage />} />
                  <Route path="in-der-naehe" element={<NearbyPage />} />
                  <Route path="darten-in" element={<PlacesIndexPage />} />
                  <Route path="darten-in/:slug" element={<PlaceLandingPage />} />
                  <Route path="dartvereine-in/:slug" element={<PlaceLandingPage category="club" />} />
                  <Route path="dart-spielstaetten-in/:slug" element={<PlaceLandingPage category="pub" />} />
                  <Route path="dart-shops-in/:slug" element={<PlaceLandingPage category="shop" />} />
                  <Route path="dart-turniere-in/:slug" element={<PlaceLandingPage category="tournament" />} />
                  <Route path="dart-turnierserien-in/:slug" element={<PlaceLandingPage category="series" />} />
                  <Route path="dartligen-in/:slug" element={<PlaceLandingPage category="league" />} />
                  <Route path="dartverbaende-in/:slug" element={<PlaceLandingPage category="association" />} />
                  <Route path="statistiken" element={<DcStatsPage />} />
                  <Route path="statistiken/pro" element={<StatsExplorerPage />} />
                  <Route path="statistiken/spieler/:playerId" element={<PlayerExplorerPage />} />
                  <Route path="statistiken/saison/:seasonStageId" element={<SeasonStatsPage />} />
                  <Route path="statistiken/:tournamentId" element={<DcStatsPage />} />
                  <Route path="vereine" element={<ClubsPage />} />
                  <Route path="verein/:slug" element={<ClubDetailPage />} />
                  <Route path="ligen" element={<OrgListingsPage type="league" />} />
                  <Route path="liga/:slug" element={<OrgListingDetailPage type="league" />} />
                  <Route path="verbaende" element={<OrgListingsPage type="association" />} />
                  <Route path="verband/:slug" element={<OrgListingDetailPage type="association" />} />
                  <Route path="spielstaetten" element={<PubsPage />} />
                  <Route path="spielstaette/:slug" element={<PubDetailPage />} />
                  <Route path="shops" element={<ShopsPage />} />
                  <Route path="shop/:slug" element={<ShopDetailPage />} />
                  <Route path="turnierserien" element={<SeriesPage />} />
                  <Route path="turnierserie/:slug" element={<SeriesDetailPage />} />
                  <Route path="ranglisten" element={<RankingsPage />} />
                  <Route path="ranglisten/:slug" element={<RankingsPage />} />
                  <Route path="info" element={<InfoPagesPage />} />
                  <Route path="info/:slug" element={<InfoPageDetailPage />} />
                  <Route path="hilfe" element={<HelpCenterPage />} />
                  <Route path="hilfe/:slug" element={<HelpArticlePage />} />
                  <Route path="kalender" element={<EventsTimelinePage />} />
                  <Route path="forum" element={<ForumPage />} />
                  <Route path="forum/topic/:slug" element={<ForumTopicPage />} />
                  <Route path="forum/:categorySlug" element={<ForumPage />} />
                  <Route
                    path="admin"
                    element={
                      <AdminRoute>
                        <AdminDashboardPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/sitemap"
                    element={
                      <AdminRoute>
                        <AdminSitemapPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/news-automation"
                    element={
                      <AdminRoute>
                        <AdminNewsAutomationPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/bot-erkennung"
                    element={
                      <AdminRoute>
                        <AdminBotDetectionPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/claude-sessions"
                    element={
                      <AdminRoute>
                        <AdminClaudeSessionsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/journal"
                    element={
                      <AdminRoute>
                        <AdminDailyJournalPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/waechter"
                    element={
                      <AdminRoute>
                        <AdminWatchdogPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/kontaktanfragen"
                    element={
                      <AdminRoute>
                        <AdminContactRequestsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/live-blogs"
                    element={
                      <AdminRoute>
                        <AdminLiveBlogsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/news-artikel"
                    element={
                      <AdminRoute>
                        <AdminNewsArticlesPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/pro-turniere"
                    element={
                      <AdminRoute>
                        <AdminProTournamentsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/spieler-verknuepfung"
                    element={
                      <AdminRoute>
                        <AdminPlayerMatchingPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/ranglisten"
                    element={
                      <AdminRoute>
                        <AdminProRankingsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/info-seiten"
                    element={
                      <AdminRoute>
                        <AdminInfoPagesPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/push-kampagnen"
                    element={
                      <AdminRoute>
                        <AdminPushCampaignsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/email-kampagnen"
                    element={
                      <AdminRoute>
                        <AdminEmailCampaignsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/planung"
                    element={
                      <AdminRoute>
                        <AdminPlanningPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/planung/:id"
                    element={
                      <AdminRoute>
                        <AdminPlanningCategoryPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/tippspiele"
                    element={
                      <AdminRoute>
                        <AdminPredictionGamesPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/tippspiele/:id"
                    element={
                      <AdminRoute>
                        <AdminPredictionGameDetailPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/hilfe"
                    element={
                      <AdminRoute>
                        <AdminHelpCenterPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/hilfe/artikel/:id"
                    element={
                      <AdminRoute>
                        <AdminHelpArticleEditorPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/seiten"
                    element={
                      <AdminRoute>
                        <AdminPagesPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/seiten/:id"
                    element={
                      <AdminRoute>
                        <AdminPageEditorPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/slider"
                    element={
                      <AdminRoute>
                        <AdminSlidersPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/slider/:id"
                    element={
                      <AdminRoute>
                        <AdminSliderDetailPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/konfiguration"
                    element={
                      <AdminRoute>
                        <AdminConfigPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/speicher"
                    element={
                      <AdminRoute>
                        <AdminServerStoragePage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/drawer"
                    element={
                      <AdminRoute>
                        <AdminDrawerPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/sicherungen"
                    element={
                      <AdminRoute>
                        <AdminBackupPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/changelog"
                    element={
                      <AdminRoute>
                        <AdminChangelogPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/content"
                    element={
                      <AdminRoute>
                        <AdminContentSectionsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/seo"
                    element={
                      <AdminRoute>
                        <AdminSeoPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/analytics"
                    element={
                      <AdminRoute>
                        <AdminAnalyticsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/turniere"
                    element={
                      <AdminRoute>
                        <AdminTournamentsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/vereine"
                    element={
                      <AdminRoute>
                        <AdminListingsPage type="club" />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/spielstaetten"
                    element={
                      <AdminRoute>
                        <AdminListingsPage type="pub" />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/shops"
                    element={
                      <AdminRoute>
                        <AdminListingsPage type="shop" />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/ligen"
                    element={
                      <AdminRoute>
                        <AdminListingsPage type="league" />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/verbaende"
                    element={
                      <AdminRoute>
                        <AdminListingsPage type="association" />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/posts"
                    element={
                      <AdminRoute>
                        <AdminPostsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/turniersoftware"
                    element={
                      <AdminRoute>
                        <AdminTournamentOpsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/push-nachrichten"
                    element={
                      <AdminRoute>
                        <AdminPushMessagesPage />
                      </AdminRoute>
                    }
                  />
                  <Route path="admin/benutzer" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
                  <Route path="admin/dart-rooms" element={<AdminRoute><AdminDartRoomsPage /></AdminRoute>} />
                  <Route path="admin/setups" element={<AdminRoute><AdminSetupsPage /></AdminRoute>} />
                  <Route
                    path="admin/meldungen"
                    element={
                      <AdminRoute>
                        <AdminReportsPage />
                      </AdminRoute>
                    }
                  />
                  <Route path="feed" element={<FeedPage />} />
                  <Route path="beitrag/:id" element={<PostDetailPage />} />
                  <Route path="community" element={<CommunityPage />} />
                  <Route path="community/profil/:username" element={<PublicProfilePage />} />
                  <Route path="community/lobbys" element={<LobbiesPage />} />
                  <Route path="community/lobby/:lobbyId" element={<LobbyDetailPage />} />
                  <Route path="news" element={<NewsPage />} />
                  <Route path="news/:slug" element={<NewsDetailPage />} />
                  <Route path="spieler" element={<PlayersDirectoryPage />} />
                  <Route path="spieler/:slug" element={<PlayerProfilePage />} />
                  <Route path="login" element={<LoginPage />} />
                  <Route path="register" element={<RegisterPage />} />
                  <Route path="passwort-vergessen" element={<ForgotPasswordPage />} />
                  <Route
                    path="profil"
                    element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="favoriten"
                    element={
                      <ProtectedRoute>
                        <FavoritesPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Alt-/Alias-URLs "meine/*" → bestehende Seiten (Profil "Meine Inhalte") */}
                  <Route path="meine/turniere" element={<Navigate to="/verwaltung?tab=tournaments" replace />} />
                  <Route path="meine/serien" element={<Navigate to="/verwaltung?tab=series" replace />} />
                  <Route path="meine/vereine" element={<Navigate to="/verwaltung?tab=clubs" replace />} />
                  <Route path="meine/spielstaetten" element={<Navigate to="/verwaltung?tab=pubs" replace />} />
                  <Route path="meine/shops" element={<Navigate to="/verwaltung?tab=shops" replace />} />
                  <Route path="meine/ligen" element={<Navigate to="/verwaltung?tab=leagues" replace />} />
                  <Route path="meine/verbaende" element={<Navigate to="/verwaltung?tab=associations" replace />} />
                  <Route path="meine/favoriten" element={<Navigate to="/favoriten" replace />} />
                  <Route
                    path="meine-anmeldungen"
                    element={
                      <ProtectedRoute>
                        <MyRegistrationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="meine-anmeldungen/:entryId/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditRegistrationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="nachrichten"
                    element={
                      <ProtectedRoute>
                        <ChatsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="nachrichten/:chatId"
                    element={
                      <ProtectedRoute>
                        <ChatsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung"
                    element={
                      <ProtectedRoute>
                        <ManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/verein/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateClubPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/verein/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditClubPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/spielstaette/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreatePubPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/spielstaette/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditPubPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/shop/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateShopPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/shop/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditShopPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/liga/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateOrgListingPage type="league" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/liga/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditOrgListingPage type="league" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/verband/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateOrgListingPage type="association" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/verband/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditOrgListingPage type="association" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnierserie/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateSeriesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnierserie/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditSeriesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnier/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateTournamentPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnier/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditTournamentPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnier/:id/email-vorlagen"
                    element={
                      <ProtectedRoute>
                        <TournamentEmailTemplatesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnier/:id/anmeldungen"
                    element={
                      <ProtectedRoute>
                        <ManageEntriesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnier/:id/team-anmeldungen"
                    element={
                      <ProtectedRoute>
                        <ManageTeamEntriesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnier/:id/betrieb"
                    element={
                      <ProtectedRoute>
                        <TournamentOpPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>
                {/* Turniersoftware-Vollbild-Views: Navbar sichtbar,
                    per ?noNavbar=true ausblendbar (Board/Beamer) */}
                <Route element={<LiveLayout />}>
                  <Route path="/scorer" element={<ScorerPage />} />
                  <Route path="/live/:operationId" element={<LiveViewPage />} />
                  <Route path="/live/:operationId/match/:matchId" element={<LiveMatchPage />} />
                </Route>
              </Routes>
              </Suspense>

              {/* Maintenance Overlay */}
              <MaintenanceOverlay
                isOpen={isInitialized && !isBackendHealthy}
                onRetry={handleRetry}
              />
            </div>
           </SearchExplorerProvider>
          </AuthProvider>
        </Router>
      </NextUIProvider>
     </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;